/**
 * Path sampling — compute points and tangent angles along a geometric
 * curve (Line / Circle / Arc / LWPOLYLINE / heavy 2D `POLYLINE`). Used by
 * ARRAYPATH to distribute entity copies along a selected path.
 *
 * ## The heavy `POLYLINE` arm is a PROJECTION, not a second sampler
 *
 * @codex PR #785 round 14 ("Accept 2D heavy polylines as ARRAYPATH curves"):
 * a heavy 2D `POLYLINE`'s planar geometry is vertices plus per-vertex group-42
 * bulges — EXACTLY `LWPOLYLINE`'s geometry — so the two families share ONE
 * segment builder here ({@link polylineSegments}, which takes the flattened
 * `{x, y, bulge}` chain rather than an entity) and one `samplePathAtDistance`
 * walk. `./heavyPolylineDrawnChain.ts` does the flattening, through `cad-core`'s
 * `heavyPolylineRuns` topology truth; `./heavyPolylineCurveRefusal.ts` decides
 * which subtypes are admitted at all (a mesh has no single traversal; a 3D
 * polyline's arc length is not its XY shadow's). Identical vertices and bulges
 * in either family therefore produce BIT-IDENTICAL samples by construction —
 * pinned as a metamorphic property in
 * `./pathSampling.heavyPolyline.metamorphic.test.ts`.
 *
 * ## Samples come out in WCS, whatever plane the entity stores
 *
 * @codex PR #785 round 15, P2: a CIRCLE / ARC / LWPOLYLINE / 2D heavy POLYLINE
 * stores OBJECT coordinates in its group-210 plane, and ARRAYPATH places its
 * clones against entities that are in WCS. Every arc-length walk below
 * therefore runs in the entity's OWN plane — where its coordinates and its
 * group-42 bulges are defined — and the finished sample is re-expressed in WCS
 * on the way out, through the one-truth Arbitrary Axis basis `drawing-model`
 * owns. See {@link pathOcsPlane} for why that order.
 *
 * ## …but a sample is still a PLAN point, and that is now DECLINED, not dropped
 *
 * @codex PR #785 round 16, P2: `PathSample.point` is `{x, y}`, so for a path
 * whose plane is TILTED out of WCS XY the lifted sample's Z is dropped and the
 * copies would follow the curve's plan shadow — foreshortened where the plane
 * leans, collapsed to a single point where it stands on edge. ARRAYPATH now
 * REFUSES such a path through {@link arrayPathPlaneRefusal} instead of placing
 * copies along the shadow, the same VISIBLE decline
 * `./heavyPolylineCurveRefusal.ts` already makes for a 3D polyline and for the
 * same underlying gap. The sampler itself is unchanged by that: it still
 * answers for any entity it is handed, because the refusal is the COMMAND's
 * gate, not the walk's.
 *
 * Angle conventions follow AutoCAD / DXF:
 *   - All tangentAngle results are in degrees.
 *   - CCW positive rotation: tangent at angle θ along a circle centered
 *     at (cx, cy) points in direction (θ + 90°).
 *   - For a directed line segment from A → B, tangentAngle =
 *     atan2(B.y - A.y, B.x - A.x) * 180 / π (range (-180, 180]).
 *
 * ## Exported surface
 *
 * export type PathSample = { point: Point2D; tangentAngle: number };
 * export type PathEntity = LineEntity | ArcEntity | CircleEntity |
 *                          LwpolylineEntity | PolylineEntity;
 *
 * export function pathTotalLength(entity: PathEntity): number
 * export function samplePathDivide(entity: PathEntity, count: number):
 *   PathSample[]
 * export function samplePathMeasure(entity: PathEntity, spacing: number):
 *   PathSample[]
 * export function samplePathAtDistance(entity: PathEntity, dist: number):
 *   PathSample
 *
 * ## Function dependency map
 *
 * pathTotalLength(entity)
 *   -> Line: Math.hypot(end - start)
 *   -> Circle: 2π * radius
 *   -> Arc: radius * |endAngle - startAngle| (CCW wraparound)
 *   -> LWPOLYLINE / heavy 2D POLYLINE: sum of segmentLength(i) for each segment
 *
 * samplePathDivide(entity, count)
 *   -> total = pathTotalLength(entity)
 *   -> step = total / (count - 1)
 *   -> samplePathAtDistance(entity, i * step) for i in [0, count)
 *
 * samplePathMeasure(entity, spacing)
 *   -> total = pathTotalLength(entity)
 *   -> n = Math.floor(total / spacing) + 1
 *   -> samplePathAtDistance(entity, i * spacing) for i in [0, n)
 *
 * samplePathAtDistance(entity, dist)
 *   -> Line: interpolate start -> end, tangent = constant
 *   -> Circle: param angle = startAngle + (dist/radius) * (180/π)
 *   -> Arc: clamp angle within [startAngle, endAngle]
 *   -> LWPOLYLINE / heavy 2D POLYLINE: walk segments until cumulative >= dist
 *       -> Straight: linear interp, tangent = atan2(dy, dx)
 *       -> Bulge: arc-segment interp via bulgeToArcSegment(seg)
 */

import { bulgeArc, type PolylineEntity } from "@dxf-ai-editor/drawing-model";

import { heavyPolylineDrawnChain } from "./heavyPolylineDrawnChain";
import { liftSampleToWcs, pathOcsPlane } from "./pathSampleOcsFrame";
import { heavyPolylineCurveRefusal } from "./heavyPolylineCurveRefusal";
import type { PathEntity, PathSample } from "./pathSampleTypes";

/**
 * `PathEntity` / `PathSample` are DECLARED in `./pathSampleTypes.ts` and
 * re-exported here, unchanged, as part of this module's surface — the barrel
 * (`export * from "./pathSampling"`) and every `from "./pathSampling"`
 * importer therefore keep the exact path they had. The declarations moved
 * because `./pathSampleOcsFrame.ts`, which this module CALLS for values,
 * states its own signatures in terms of them: declaring them here made the
 * pair a cycle that `.dependency-cruiser.cjs`'s `no-circular` rule rejects
 * (it has no type-only exception). See that leaf's header.
 */
export type { PathEntity, PathSample };

/**
 * The FRAME half of ARRAYPATH's eligibility — "is this curve confined to a
 * plane parallel to WCS XY, where this sampler's two-axis answer is the curve
 * itself rather than its plan shadow?" — declared in
 * `./arrayPathPlaneRefusal.ts` and re-exported here for the same reason
 * `PathEntity` / `PathSample` are: the package barrel reaches this module
 * (`export * from "./pathSampling"`) and a consumer of the sampler is exactly
 * the consumer that has to ask the question. The predicate lives in its own
 * leaf because the answer is not the WALK's truth, the same split
 * `./heavyPolylineCurveRefusal.ts` (the SUBTYPE half of the same eligibility)
 * already is. @codex PR #785 round 16, P2.
 */
export {
  arrayPathPlaneRefusal,
  type ArrayPathPlaneRefusal,
} from "./arrayPathPlaneRefusal";

type Point2D = { x: number; y: number };

/**
 * The flattened planar chain BOTH polyline families present to this module:
 * an ordered vertex list whose `bulge` is the DXF group-42 of the span LEAVING
 * that vertex, plus whether the run wraps. `LwpolylineEntity` satisfies it
 * as-is (its own `vertices` carry `x` / `y` / `bulge` and it has `closed`), and
 * `./heavyPolylineDrawnChain.ts` produces it for a heavy `POLYLINE` — so the
 * segment builder below is written ONCE against the shape rather than twice
 * against two entity types.
 */
interface PolylineChainView {
  readonly vertices: readonly {
    readonly x: number;
    readonly y: number;
    /**
     * DXF group 42 — REQUIRED, never optional. `lwpolylineEntitySchema`'s own
     * `polylineVertexSchema` materialises it with `.default(0)`, and
     * `HeavyPolylineChainVertex` declares it `number`, so BOTH producers
     * already hand over a resolved value; admitting `undefined` here would add
     * a `?? 0` this module can never exercise (and a mutant no test can kill).
     */
    readonly bulge: number;
  }[];
  readonly closed: boolean;
}

const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;
const TAU = Math.PI * 2;

// ── Arc helpers ──────────────────────────────────────────────────────

/**
 * Normalize a pair of angles in degrees to a positive CCW sweep
 * `[startAngle, endAngle)` where the sweep is always in [0, 360).
 * AutoCAD Arc entities use startAngle / endAngle in degrees with CCW
 * orientation; when endAngle < startAngle the arc crosses 0° and must
 * have 360° added to the delta.
 */
function arcSweepDeg(startDeg: number, endDeg: number): number {
  let delta = endDeg - startDeg;
  while (delta <= 0) delta += 360;
  while (delta > 360) delta -= 360;
  return delta;
}

// ── Bulge helpers ────────────────────────────────────────────────────

/**
 * Convert a polyline bulge value into a circular-arc segment between
 * two vertices. `bulge = tan(θ/4)` where θ is the signed included
 * angle of the arc (positive = CCW from v1 to v2). A bulge of 0 means
 * a straight segment.
 *
 * Returns the arc length and a function `pointAtParam(u)` that maps
 * u ∈ [0, 1] to a point/tangent along the bulge. The tangent angle is
 * in degrees (CCW positive).
 */
type BulgeSegment = {
  length: number;
  pointAtParam: (u: number) => PathSample;
};

function straightSegment(v1: Point2D, v2: Point2D): BulgeSegment {
  const dx = v2.x - v1.x;
  const dy = v2.y - v1.y;
  const len = Math.hypot(dx, dy);
  const tangentAngle = Math.atan2(dy, dx) * RAD2DEG;
  return {
    length: len,
    pointAtParam: (u: number) => ({
      point: { x: v1.x + dx * u, y: v1.y + dy * u },
      tangentAngle,
    }),
  };
}

function bulgeSegment(v1: Point2D, v2: Point2D, bulge: number): BulgeSegment {
  // AutoCAD bulge convention (per DXF reference, group 42):
  //   bulge = tan(θ / 4)  where θ is the arc's included angle
  //   the bulge is NEGATIVE iff the arc runs CLOCKWISE from v1 to v2, so
  //   bulge > 0 → arc swept COUNTER-CLOCKWISE about its centre; the centre
  //               sits to the LEFT of the chord direction v1→v2 and the arc
  //               itself therefore bows to the RIGHT of it (Y-up frame)
  //   bulge < 0 → the mirror image
  //
  // Reference case used throughout: v1=(0,0) v2=(20,0) bulge=+1 (half-circle,
  // θ=π) → LOWER half-circle, apex (10,−10), centre on the chord at (10,0) —
  // exactly ezdxf 1.4.4 `bulge_to_arc` (centre (10,0), 180°→0° traversed CCW).
  //
  // One-truth: the arc centre / radius / start-angle / signed-sweep are derived
  // by `bulgeArc` in `drawing-model/bulgeArc.ts` (shared with the viewport
  // membership AABB). This sampler only adds the traversal TANGENT, a sampling
  // concern that belongs here. `bulgeArc` returns null for a straight /
  // degenerate (coincident-vertex) segment, which falls back to a chord.
  const arc = bulgeArc(v1, v2, bulge);
  if (!arc) {
    return straightSegment(v1, v2);
  }

  // `signedSweep` carries the sign of the bulge: positive ⇒ CCW about the
  // centre, so the traversal tangent is the radius vector rotated by +90°
  // (and by −90° for the clockwise, negative-bulge case).
  return {
    length: arc.length,
    pointAtParam: (u: number) => {
      const angle = arc.startAngle + arc.signedSweep * u;
      const px = arc.cx + arc.radius * Math.cos(angle);
      const py = arc.cy + arc.radius * Math.sin(angle);
      const tangentRad =
        arc.signedSweep > 0 ? angle + Math.PI / 2 : angle - Math.PI / 2;
      return {
        point: { x: px, y: py },
        tangentAngle: tangentRad * RAD2DEG,
      };
    },
  };
}

/**
 * Sample the INTERMEDIATE points of a single bulge segment at roughly
 * `spacing` arc-length intervals (endpoints excluded — callers already
 * carry the segment's start/end vertices). Returns `[]` for straight
 * segments (bulge≈0), degenerate chords, or non-positive spacing.
 *
 * REVCLOUD-MODES: exported for `revisionCloudOutline.ts` (Object-mode
 * source tracing subdivides bulged polyline segments into ~chord-length
 * steps so every rendered cloud edge carries one arc bump). One-truth:
 * reuses the same `bulgeSegment` math the ARRAYPATH sampler uses.
 */
export function sampleBulgeIntermediatePoints(
  v1: Point2D,
  v2: Point2D,
  bulge: number,
  spacing: number,
): Point2D[] {
  if (Math.abs(bulge) < 1e-12 || spacing <= 0) return [];
  const seg = bulgeSegment(v1, v2, bulge);
  if (seg.length < 1e-12) return [];
  const steps = Math.max(1, Math.round(seg.length / spacing));
  const out: Point2D[] = [];
  for (let i = 1; i < steps; i++) {
    out.push(seg.pointAtParam(i / steps).point);
  }
  return out;
}

/**
 * Midpoint of a single polyline segment from `v1` to `v2`.
 *
 * For a straight segment (`bulge` ≈ 0) this is the Euclidean chord
 * midpoint. For a bulged (arc) segment it is the point on the arc at
 * half its arc-length (u = 0.5), i.e. the AutoCAD-style segment-midpoint
 * grip that sits ON the bump rather than on the chord.
 *
 * One-truth: delegates to the same `bulgeSegment` math used by every
 * other polyline sampler in this module — callers (the GRIPS subsystem,
 * §5.11) must NOT re-derive bulge arc geometry.
 */
export function bulgeMidpoint(
  v1: Point2D,
  v2: Point2D,
  bulge = 0,
): Point2D {
  return bulgeSegment(v1, v2, bulge).pointAtParam(0.5).point;
}

/**
 * Parse the LWPOLYLINE `Bulges` property (`"0;0.41;0"` or `"0,0.41,0"`)
 * into a per-segment bulge list. Tolerant of both `;` and `,` separators
 * so a producer that emits per-segment bulges round-trips. Returns an
 * empty array when the raw string is absent; non-finite tokens map to 0.
 *
 * One-truth for the GRIPS subsystem (§5.11): both the grip RENDER path
 * (`bulgeMidpoint` at render) and the grip DRAG path must read the same
 * bulge list so the seg-mid drag delta is measured from the SAME midpoint
 * the grip is drawn at (otherwise an arc segment jumps by the sagitta).
 */
export function parseBulgeList(raw: string | undefined): number[] {
  if (!raw) return [];
  return raw
    .split(/[;,]/)
    .map((s) => Number(s.trim()))
    .map((n) => (Number.isFinite(n) ? n : 0));
}

// ── LWPOLYLINE segmentation ──────────────────────────────────────────

/**
 * Build a list of segments (straight or bulge-arc) for the given
 * polyline. Closed polylines include an implicit closing segment from
 * the last vertex back to the first.
 */
function polylineSegments(chain: PolylineChainView): BulgeSegment[] {
  const segs: BulgeSegment[] = [];
  const vs = chain.vertices;
  for (let i = 1; i < vs.length; i++) {
    const v1 = vs[i - 1]!;
    const v2 = vs[i]!;
    // AutoCAD bulge is stored on the *starting* vertex of each segment.
    segs.push(bulgeSegment(v1, v2, v1.bulge));
  }
  if (chain.closed && vs.length > 2) {
    const v1 = vs[vs.length - 1]!;
    const v2 = vs[0]!;
    segs.push(bulgeSegment(v1, v2, v1.bulge));
  }
  return segs;
}

/**
 * The chain a heavy `POLYLINE` presents to this module, or `null` when it has
 * none that ARRAYPATH may sample.
 *
 * TWO gates, and both are load-bearing. {@link heavyPolylineCurveRefusal} is
 * the ELIGIBILITY truth — a polygon / poly-face mesh is not a curve at all, and
 * a 3D polyline's arc length is not the length of its XY shadow, so sampling
 * either would return confidently wrong numbers rather than none.
 * {@link heavyPolylineDrawnChain} is the GEOMETRY truth — which records the
 * entity actually draws, in what order, with which bulges. A refused or
 * chain-less polyline yields NO segments, which `pathTotalLength` reports as a
 * zero-length path and `samplePathAtDistance` as the entity's own first
 * recorded point: the same degenerate answers this module already gives an
 * empty LWPOLYLINE, never an invented curve.
 */
function heavyPolylineChainView(
  entity: PolylineEntity,
): PolylineChainView | null {
  // EQUIVALENT MUTANT (targeted-cad-mutation-testing's equivalent-mutant
  // contract), StringLiteral `"array-path"` -> `""`:
  // `heavyPolylineCurveRefusal` tests the role POSITIVELY, `role === "offset"`,
  // and every refusal outside that branch is role-blind (mesh, 3D polyline). So
  // `""` and `"array-path"` return the same value for every entity and no test
  // can discriminate them; `HeavyPolylineCurveRole` makes `""` unreachable in
  // real code, only a mutant can produce it. (The same shape, for the same
  // reason, as `./heavyPolylineTrim.ts`'s documented `"edge"` -> `""`.)
  //
  // NOTE: the directive must sit DIRECTLY above the mutated string's OWN line.
  // Stryker disable next-line StringLiteral: the predicate branches on `role === "offset"` only, so every non-"offset" string behaves identically to "array-path" (see equivalent-mutant note above)
  if (heavyPolylineCurveRefusal(entity, "array-path") !== null) return null;
  return heavyPolylineDrawnChain(entity);
}

// ── Total length ─────────────────────────────────────────────────────

/**
 * Total arc length of the given path entity. For a circle this is the
 * full circumference (2·π·r). For an arc, the sweep length
 * (r · θ_rad). For lines, the Euclidean distance. For polylines, the
 * sum of segment lengths (including bulge arcs and the implicit
 * closing segment if closed).
 */
export function pathTotalLength(entity: PathEntity): number {
  switch (entity.type) {
    case "Line": {
      const dx = entity.end.x - entity.start.x;
      const dy = entity.end.y - entity.start.y;
      return Math.hypot(dx, dy);
    }
    case "Circle":
      return TAU * entity.radius;
    case "Arc": {
      const sweepDeg = arcSweepDeg(entity.startAngle, entity.endAngle);
      return entity.radius * sweepDeg * DEG2RAD;
    }
    case "LWPOLYLINE":
      return polylineChainLength(entity);
    case "Polyline":
      return polylineChainLength(heavyPolylineChainView(entity));
  }
}

/**
 * Summed arc length of a chain's segments — the ONE fold both polyline
 * families use. A `null` chain (a heavy polyline this command declines, or one
 * with no drawable run) is a ZERO-length path, the same answer an empty
 * LWPOLYLINE already gets.
 */
function polylineChainLength(chain: PolylineChainView | null): number {
  if (!chain) return 0;
  let total = 0;
  for (const seg of polylineSegments(chain)) total += seg.length;
  return total;
}

// ── Sampling at a distance ───────────────────────────────────────────

/**
 * Produce a single sample (point + tangent direction in degrees) at
 * the given arc-length `dist` from the path's start. The `dist` is
 * clamped to `[0, pathTotalLength(entity)]` — distances past the end
 * return the endpoint sample, and negative distances return the start
 * sample.
 *
 * The returned point and tangent are **WCS**, for every path kind — see
 * {@link pathOcsPlane}. The walk itself runs in the entity's own plane, which
 * is where its stored coordinates and its group-42 bulges mean what they say.
 */
export function samplePathAtDistance(
  entity: PathEntity,
  dist: number,
): PathSample {
  const plane = pathOcsPlane(entity);
  const sample = samplePathAtDistanceInPlane(entity, dist);
  return plane ? liftSampleToWcs(sample, plane) : sample;
}

/**
 * The same sample, expressed in the entity's OWN plane — the walk this module
 * has always done. Private: an OCS sample is a frame nobody outside this file
 * can interpret without the entity beside it, which is exactly the defect
 * {@link pathOcsPlane} documents.
 */
function samplePathAtDistanceInPlane(
  entity: PathEntity,
  dist: number,
): PathSample {
  const total = pathTotalLength(entity);
  const clamped = Math.max(0, Math.min(dist, total));

  switch (entity.type) {
    case "Line": {
      if (total < 1e-12) {
        return {
          point: { x: entity.start.x, y: entity.start.y },
          tangentAngle: 0,
        };
      }
      const u = clamped / total;
      const dx = entity.end.x - entity.start.x;
      const dy = entity.end.y - entity.start.y;
      return {
        point: {
          x: entity.start.x + dx * u,
          y: entity.start.y + dy * u,
        },
        tangentAngle: Math.atan2(dy, dx) * RAD2DEG,
      };
    }
    case "Circle": {
      // A full circle sampled from "start = angle 0" going CCW.
      const angleRad = clamped / entity.radius;
      const px = entity.center.x + entity.radius * Math.cos(angleRad);
      const py = entity.center.y + entity.radius * Math.sin(angleRad);
      // Tangent to CCW circle at angle θ is (θ + 90°).
      return {
        point: { x: px, y: py },
        tangentAngle: (angleRad + Math.PI / 2) * RAD2DEG,
      };
    }
    case "Arc": {
      // arc length = radius * sweepRad; so deltaRad = clamped / radius
      const sweepDeg = arcSweepDeg(entity.startAngle, entity.endAngle);
      const sweepRad = sweepDeg * DEG2RAD;
      const deltaRad = entity.radius > 1e-12 ? clamped / entity.radius : 0;
      const cappedRad = Math.min(deltaRad, sweepRad);
      const angleRad = entity.startAngle * DEG2RAD + cappedRad;
      const px = entity.center.x + entity.radius * Math.cos(angleRad);
      const py = entity.center.y + entity.radius * Math.sin(angleRad);
      return {
        point: { x: px, y: py },
        tangentAngle: (angleRad + Math.PI / 2) * RAD2DEG,
      };
    }
    case "LWPOLYLINE":
      return samplePolylineChainAtDistance(
        entity,
        entity.vertices[0] ?? { x: 0, y: 0 },
        clamped,
      );
    case "Polyline": {
      // The FIRST RECORD's own location is the degenerate fallback, matching
      // the LWPOLYLINE arm's `vertices[0]` — the chain view is `null` exactly
      // when there is no curve, and a record's `location` is the heavy
      // family's spelling of the same pair.
      const first = entity.vertices[0]?.location ?? { x: 0, y: 0 };
      return samplePolylineChainAtDistance(
        heavyPolylineChainView(entity),
        first,
        clamped,
      );
    }
  }
}

/**
 * The ONE polyline walk both families share: step along the chain's segments
 * until `clamped` arc-length is consumed, then interpolate inside the segment
 * that holds it.
 *
 * `fallbackPoint` is the entity's own first recorded position, used when the
 * chain yields no segment at all (an empty / single-vertex LWPOLYLINE, a heavy
 * polyline this command declines). Reporting that point with a zero tangent —
 * rather than the origin, or an invented curve — is the pre-existing LWPOLYLINE
 * behaviour this extraction preserves byte for byte.
 */
function samplePolylineChainAtDistance(
  chain: PolylineChainView | null,
  fallbackPoint: { x: number; y: number },
  clamped: number,
): PathSample {
  const segs = chain ? polylineSegments(chain) : [];
  if (segs.length === 0) {
    return {
      point: { x: fallbackPoint.x, y: fallbackPoint.y },
      tangentAngle: 0,
    };
  }
  let remaining = clamped;
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i]!;
    if (remaining <= seg.length || i === segs.length - 1) {
      const u = seg.length > 1e-12 ? remaining / seg.length : 0;
      return seg.pointAtParam(Math.min(Math.max(u, 0), 1));
    }
    remaining -= seg.length;
  }
  // Unreachable — guarded by i === segs.length - 1 above.
  return segs[segs.length - 1]!.pointAtParam(1);
}

// ── Divide / Measure ─────────────────────────────────────────────────

/**
 * Divide the path into (count - 1) equal arc-length intervals and
 * return `count` samples including both endpoints. Matches AutoCAD's
 * DIVIDE command behaviour when applied to the sampling path.
 */
export function samplePathDivide(
  entity: PathEntity,
  count: number,
): PathSample[] {
  if (count < 2) {
    return [samplePathAtDistance(entity, 0)];
  }
  const total = pathTotalLength(entity);
  const step = total / (count - 1);
  const out: PathSample[] = [];
  for (let i = 0; i < count; i++) {
    out.push(samplePathAtDistance(entity, i * step));
  }
  return out;
}

/**
 * Produce samples at fixed arc-length intervals of `spacing` from the
 * path's start. The last sample may be closer to the end than
 * `spacing` (AutoCAD MEASURE semantics — remainder is truncated).
 *
 * Returns at least one sample at distance 0 even when `spacing` > total.
 */
export function samplePathMeasure(
  entity: PathEntity,
  spacing: number,
): PathSample[] {
  if (spacing <= 0) {
    return [samplePathAtDistance(entity, 0)];
  }
  const total = pathTotalLength(entity);
  const count = Math.floor(total / spacing) + 1;
  const out: PathSample[] = [];
  for (let i = 0; i < count; i++) {
    out.push(samplePathAtDistance(entity, i * spacing));
  }
  return out;
}
